import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { History, Loader2, RefreshCw, Star, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { handleApiError } from '@/utils';
import { ApiError } from '@/services/api.service';
import { useViewerReadOnly } from '@/contexts/ViewerReadOnlyContext';
import { useGetListing, usePullListing, listingQueryKey } from '@/resources/listing';
import { listingFailureLine } from '@/resources/listing/listing.events';
import { markPullInitiated } from '@/resources/listing/listing.initiator';
import type { ListingPullRefusal } from '@/types/listing';
import type { Product } from '@/types/product';
import { formatMoney, formatRating, formatReviews } from './listingFormat';
import {
  ListingGalleryMainImage,
  ListingGalleryThumbnails,
  useListingGallery,
} from './ListingImageGallery';
import { ListingVersionHistoryModal } from './ListingVersionHistoryModal';

/**
 * The listing content panel (`WEB-004`, `specs/product/listing-content-
 * panel`, `AUT-231`) -- the four states, the cooldown, the two refusals
 * and the error line that clears.
 *
 * The heading, the "Last pulled" line and the three controls sit **outside**
 * the card, and the card opens with an edge-to-edge image
 * (`design-input/listing-content-panel/prototype-extracted.md § Panel card`,
 * `AUT-297`). Putting them inside it was the original deviation from the
 * prototype and the cause of four of `AUT-231`'s QA tickets.
 *
 * `heroShow` carries price, rating and review count: those are
 * `landscape_products` data the page has already fetched, and `GET
 * …/listing` deliberately does not carry them
 * (`components/product.md § The three routes`,
 * `§ The ruling on price, rating and review count — do not reopen`).
 */
export interface ListingContentPanelProps {
  productId: number;
  brandId: number;
  asin: string;
  productTitle: string;
  heroShow: Pick<Product, 'price' | 'rating' | 'reviews'>;
  /**
   * Whether the drawer holding this panel is open. The panel stays mounted
   * when the drawer collapses -- `ProductDetail` animates a width, it does
   * not unmount -- so this is what stops `GET …/listing` and its 5 s
   * in-flight poll from running for a panel nobody can see. Defaults to
   * `true` so a caller that always shows the panel need not pass it.
   */
  active?: boolean;
  onClose: () => void;
}

const VIEWER_REASON = 'View-only accounts cannot pull listing content.';

/**
 * `en-US`, `{ month: "short", day: "numeric", year: "numeric" }` then
 * `{ hour: "numeric", minute: "2-digit" }`, joined with ` at ` --
 * `Jul 28, 2026 at 3:04 PM` (`design-input/listing-content-panel/
 * prototype-extracted.md § Timestamp format`). `captured_at`/
 * `last_pulled_at` are ISO 8601 UTC on the wire; `Date` renders them in
 * the browser's local zone (`components/product.md § Time on this
 * screen`).
 */
function formatLastPulled(lastPulledAt: string): string {
  const date = new Date(lastPulledAt);
  const datePart = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const timePart = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${datePart} at ${timePart}`;
}

function formatCooldownTime(cooldownUntil: string): string {
  return new Date(cooldownUntil).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export function ListingContentPanel({
  productId,
  brandId,
  asin,
  productTitle,
  heroShow,
  active = true,
  onClose,
}: ListingContentPanelProps) {
  const [historyOpen, setHistoryOpen] = useState(false);
  const [cooldownOverride, setCooldownOverride] = useState<string | null>(null);
  const viewerReadOnly = useViewerReadOnly();
  const queryClient = useQueryClient();

  const { data, status: queryStatus, dataUpdatedAt } = useGetListing(productId, brandId, active);
  const pullMutation = usePullListing();

  // The in-flight state is whatever the last `useGetListing` response says
  // (`data-flow.md § Flow: listing content pull`), plus the window between
  // the click and the first response that has seen it. Without that second
  // clause the panel showed nothing at all until the invalidated query came
  // back, and a pull that finished inside that window never rendered a
  // skeleton -- `AUT-300`'s "sometimes skeleton loader is not triggered".
  //
  // It is bounded by `dataUpdatedAt`, not by the mutation settling: the
  // moment ANY response lands after the click, that response decides. So a
  // still-pending POST can never hold the panel in-flight against a
  // response saying `is_pulling: false`, which is the rule the poll table
  // is built on.
  // Keyed on `submittedAt`, not on the mutation still being pending: the
  // `202` often lands before the invalidated query does, and dropping out of
  // the skeleton in that gap is the flicker `AUT-300` reports. `isError`
  // excludes the two refusals -- a `429` refuses outright and a `409` resyncs
  // through its own invalidation, and neither should paint a skeleton for a
  // pull this click did not start.
  const awaitingFirstPullResponse =
    pullMutation.submittedAt > 0 && !pullMutation.isError && dataUpdatedAt < pullMutation.submittedAt;
  const isPulling = data?.is_pulling === true || awaitingFirstPullResponse;

  // The first load has no response yet, so it knows neither "empty" nor
  // "loaded" -- rendering the empty state here is a claim the panel cannot
  // support, and it flashed "No listing content yet" on every page open
  // (`AUT-298`).
  const isLoadingFirstResponse = queryStatus === 'pending' && data === undefined;
  const showSkeleton = isPulling || isLoadingFirstResponse;

  const isEmpty = !data || data.latest_version === null;

  const gallery = useListingGallery(data?.image_urls ?? []);

  const failureLine =
    queryStatus === 'error'
      ? listingFailureLine('unavailable')
      : data && (data.status === 'not_found' || data.status === 'unavailable')
        ? listingFailureLine(data.status)
        : null;

  // A `429`'s `cooldown_until` is the resync point -- applied immediately
  // rather than waiting on another `GET …/listing` round trip
  // (`components/product.md § What the SPA does with each refusal`).
  const effectiveCooldownUntil = cooldownOverride ?? data?.cooldown_until ?? null;

  // Local UI clock only -- it recomputes whether `effectiveCooldownUntil`
  // has passed and never touches the network, so it is not the "automatic
  // recovery" the poll-stop rule forbids (that rule is about re-arming the
  // query after an error, not about noticing a known future instant has
  // arrived).
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    if (!effectiveCooldownUntil) return;
    const target = new Date(effectiveCooldownUntil).getTime();
    // `setTimeout`'s delay is a 32-bit signed int -- a cooldown more than
    // ~24 days out (should never happen given `NFR-1`'s 15-minute window,
    // but nothing rejects a wrong value at this layer) overflows and fires
    // near-instantly. Cap each hop and re-check on fire rather than trust
    // one long-delay timer.
    const MAX_DELAY_MS = 2_000_000_000;
    let timer: ReturnType<typeof setTimeout>;
    const schedule = () => {
      const remaining = target - Date.now();
      if (remaining <= 0) {
        setNowTick(Date.now());
        return;
      }
      timer = setTimeout(schedule, Math.min(remaining, MAX_DELAY_MS));
    };
    schedule();
    return () => clearTimeout(timer);
  }, [effectiveCooldownUntil]);

  const isCoolingDown = effectiveCooldownUntil !== null && new Date(effectiveCooldownUntil).getTime() > nowTick;

  const handlePull = () => {
    pullMutation.mutate(
      { productId, brandId },
      {
        onError: (error) => {
          if (error instanceof ApiError && error.status === 409) {
            // A pull is already running -- resync by invalidating rather
            // than setting a local flag, so the skeleton comes from the
            // next `is_pulling: true` response, not a client guess.
            //
            // Marked as an initiator all the same: the user asked for this
            // content and the in-flight pull is what serves that ask, so
            // the broadcast that ends it owes them the same toast it would
            // have owed a `202` (`components/product.md § What the SPA
            // does with each refusal`). Scope `any` -- unlike a `202`, this
            // tab did not start the pull, so any terminal event for the
            // product (whoever's) should spend it. A `429` gets no mark --
            // it refuses outright, nothing will finish, and it toasts the
            // refusal synchronously instead.
            markPullInitiated(productId, 'any');
            queryClient.invalidateQueries({ queryKey: listingQueryKey(productId, brandId) });
            return;
          }
          if (error instanceof ApiError && error.status === 429) {
            const refusal = error.data as ListingPullRefusal | undefined;
            if (refusal?.cooldown_until) {
              setCooldownOverride(refusal.cooldown_until);
            }
            handleApiError(error);
            return;
          }
          handleApiError(error);
        },
      },
    );
  };

  const disabledReason = isPulling
    ? null
    : viewerReadOnly
      ? VIEWER_REASON
      : isCoolingDown && effectiveCooldownUntil
        ? `Available again at ${formatCooldownTime(effectiveCooldownUntil)}`
        : null;

  const controlsDisabled = isPulling || viewerReadOnly || isCoolingDown;

  const subline = data?.last_pulled_at ? `Last pulled ${formatLastPulled(data.last_pulled_at)}` : 'Not pulled yet';

  const showActions = !isEmpty;

  return (
    <div data-testid="listing-content-panel" className="relative">
      {/* Outside the `space-y-3` block: as its first child it gave the
          heading a 12px top margin that offset the panel (`AUT-434`). */}
      <Button
        variant="ghost"
        size="icon"
        className="absolute right-0 top-0 z-10 h-7 w-7"
        aria-label="Close live listing"
        data-testid="listing-panel-close"
        onClick={onClose}
      >
        <X className="h-4 w-4" />
      </Button>

      <div className="space-y-3">
        {/* `pr-8` keeps the heading clear of the close button. */}
        <div className="space-y-1 pr-8">
          <h2
            data-testid="listing-panel-heading"
            className="break-words text-sm font-semibold uppercase tracking-wide text-foreground"
          >
            Current Amazon listing content
          </h2>

          {showSkeleton ? (
            <Skeleton className="h-4 w-48" data-testid="listing-subline-skeleton" />
          ) : (
            <p data-testid="listing-subline" className="text-xs text-muted-foreground">
              {subline}
            </p>
          )}

          {failureLine && !showSkeleton && (
            <p data-testid="listing-failure-line" className="text-xs text-status-error-strong">
              {failureLine}
            </p>
          )}

          {showSkeleton ? (
            <div
              className="flex items-center gap-1.5"
              aria-hidden="true"
              data-testid="listing-actions-skeleton"
            >
              <Skeleton className="h-6 w-20 rounded-lg" />
              <Skeleton className="h-6 w-28 rounded-lg" />
            </div>
          ) : showActions ? (
            <div className="flex items-center gap-1.5">
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-1.5"
                aria-label="Refresh listing content"
                data-testid="listing-refresh-button"
                disabled={controlsDisabled}
                onClick={handlePull}
              >
                {/* No spinning state: while a pull runs, this whole row is
                    replaced by the two skeleton pills above (`AUT-300`). */}
                <RefreshCw className="h-3.5 w-3.5" />
                Refresh
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-1.5"
                data-testid="listing-view-history-button"
                onClick={() => setHistoryOpen(true)}
              >
                <History className="h-3.5 w-3.5" />
                View history
              </Button>
            </div>
          ) : null}

          {!showSkeleton && showActions && disabledReason && (
            <p data-testid="listing-refresh-disabled-reason" className="text-xs text-muted-foreground">
              {disabledReason}
            </p>
          )}
        </div>

        {showSkeleton ? (
          <div
            className="overflow-hidden rounded-sm border border-border bg-card"
            data-testid="listing-pulling-body"
          >
            <div
              className="flex aspect-square w-full flex-col items-center justify-center gap-3 bg-surface-tint-medium"
              data-testid="listing-pulling-image"
            >
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              <p
                className="px-6 text-center text-sm text-muted-foreground"
                data-testid="listing-pulling-message"
              >
                Pulling in the current listing content for this ASIN…
              </p>
            </div>

            <div className="space-y-3 p-4" aria-hidden="true">
              <div className="flex flex-wrap items-center gap-1.5">
                {Array.from({ length: 5 }, (_, index) => (
                  <Skeleton key={index} className="h-14 w-14 rounded-sm" />
                ))}
              </div>
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
              {/* The price and rating row has a loading state too (`AUT-300`). */}
              <div className="flex items-center justify-between" data-testid="listing-pulling-price-rating">
                <Skeleton className="h-5 w-16" />
                <Skeleton className="h-4 w-20" />
              </div>
              <div className="h-px bg-border" />
              <div className="space-y-2">
                {Array.from({ length: 4 }, (_, index) => (
                  <Skeleton key={index} className="h-3 w-full" />
                ))}
              </div>
            </div>
          </div>
        ) : isEmpty ? (
          <div
            className="flex flex-col items-center justify-center gap-3 rounded-sm border border-border bg-card px-6 py-10 text-center"
            data-testid="listing-empty-state"
          >
            <div className="space-y-1">
              <h3 data-testid="listing-empty-heading" className="text-sm font-semibold text-foreground-strong">
                No listing content yet
              </h3>
              <p data-testid="listing-empty-body" className="text-xs leading-relaxed text-muted-foreground">
                Pull the current Amazon listing for {asin} to see images, title, and bullets here.
              </p>
            </div>
            <Button
              // AUT-196 (design QA 2026-10-14): "the 'Pull current listing' should also be a
              // secondary button rather than a primary button".
              variant="secondary"
              size="sm"
              data-testid="listing-pull-button"
              disabled={controlsDisabled}
              onClick={handlePull}
            >
              <RefreshCw className="h-4 w-4" />
              Pull current listing
            </Button>
            {disabledReason && (
              <p data-testid="listing-pull-disabled-reason" className="text-xs text-muted-foreground">
                {disabledReason}
              </p>
            )}
          </div>
        ) : (
          <div
            className="overflow-hidden rounded-sm border border-border bg-card"
            data-testid="listing-loaded-content"
          >
            <ListingGalleryMainImage src={gallery.mainImage} alt={data.latest_version.title} />

            <div className="space-y-3 p-4">
              <ListingGalleryThumbnails
                images={data.image_urls}
                selectedIndex={gallery.selectedIndex}
                onSelect={gallery.select}
                padToMinimumSlots
              />

              <p className="text-sm font-semibold leading-snug" data-testid="listing-title">
                {data.latest_version.title}
              </p>

              {/* One row, price left and rating right -- `AUT-297`. */}
              <div className="flex items-center justify-between">
                <span className="text-base font-semibold" data-testid="listing-price">
                  {formatMoney(heroShow.price)}
                </span>
                <span
                  className="flex items-center gap-1 text-xs text-muted-foreground"
                  data-testid="listing-rating-reviews"
                >
                  <Star className="h-3.5 w-3.5 fill-foreground text-foreground" strokeWidth={0} />
                  <span className="font-medium text-foreground">{formatRating(heroShow.rating)}</span>
                  <span>({formatReviews(heroShow.reviews)})</span>
                </span>
              </div>

              <div className="h-px bg-border" />

              <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                About this item
              </h4>
              <ul className="space-y-2" data-testid="listing-bullets">
                {data.latest_version.bullets.map((bullet, index) => (
                  <li key={index} className="flex gap-2 text-xs leading-relaxed" data-testid={`listing-bullet-${index}`}>
                    <span aria-hidden="true" className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-muted-foreground" />
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        <ListingVersionHistoryModal
          open={historyOpen}
          onOpenChange={setHistoryOpen}
          productId={productId}
          brandId={brandId}
          asin={asin}
          productTitle={productTitle}
          heroShow={heroShow}
        />
      </div>
    </div>
  );
}
